import { Container, IContentEvaluation } from "@amzn/ask-legal-domain";
import { ContentEvaluationModel } from "../model/content-evaluation-model";
import { FlashbarProps } from "@amzn/awsui-components-react";

export class ContentEvaluationUtils {
    static async exportEvaluationResults(
        results: IContentEvaluation.SubmitContentEvaluationOutput | null,
        containerEvaluationState: ContentEvaluationModel.ContainerEvaluationState,
        selectedContainerIds: string[] | undefined,
        containerMetadata: {[key: string]: Container.Data} | undefined,
        pageName: string,
        onFlashbarUpdated?: (flashbarProps: Pick<FlashbarProps, "items"> | undefined) => void
    ): Promise<void> {
        try {
            const allFindings = this.collectAllFindings(
                results,
                containerEvaluationState,
                selectedContainerIds,
                containerMetadata
            );

            if (allFindings.length === 0) {
                onFlashbarUpdated?.({
                    items: [{
                        type: "info",
                        content: "No findings found to export",
                        dismissible: true,
                        onDismiss: () => onFlashbarUpdated?.(undefined)
                    }]
                });
                return;
            }

            const { CSVFactory } = await import("../factory/file/csv-factory");
            const headers = this.getCSVHeaders(selectedContainerIds);
            const csvContent = CSVFactory.objectsToCSV(allFindings, headers);
            const filename = this.generateFilename(pageName, selectedContainerIds);

            await CSVFactory.downloadCSV(csvContent, filename);

            const successMessage = `CSV file exported successfully with ${allFindings.length} findings`;
            onFlashbarUpdated?.({
                items: [{
                    type: "success",
                    content: successMessage,
                    dismissible: true,
                    onDismiss: () => onFlashbarUpdated?.(undefined)
                }]
            });
        } catch (error) {
            onFlashbarUpdated?.({
                items: [{
                    type: "error",
                    content: `Failed to export CSV: ${error.message || "Unknown error"}`,
                    dismissible: true,
                    onDismiss: () => onFlashbarUpdated?.(undefined)
                }]
            });
        }
    }

    private static collectAllFindings(
        results: IContentEvaluation.SubmitContentEvaluationOutput | null,
        containerEvaluationState: ContentEvaluationModel.ContainerEvaluationState,
        selectedContainerIds: string[] | undefined,
        containerMetadata: {[key: string]: Container.Data} | undefined
    ): ExportEvaluationFinding[] {
        const allFindings: ExportEvaluationFinding[] = [];

        // Multi-container evaluation
        if (selectedContainerIds) {
            selectedContainerIds.forEach(containerId => {
                const containerName = containerMetadata?.[containerId]?.title || containerId;
                const containerState = containerEvaluationState[containerId];

                if (containerState?.result?.data?.findings) {
                    containerState.result.data.findings.forEach((finding: IContentEvaluation.EvaluationFinding) => {
                        allFindings.push({
                            containerName: containerName,
                            flaggedRule: finding.flaggedRule,
                            flaggedPassage: finding.flaggedPassage,
                            description: finding.description,
                            suggestedPassages: Array.isArray(finding.suggestedPassages)
                                ? finding.suggestedPassages.join("; ")
                                : (finding.suggestedPassages || "")
                        });
                    });
                }
            });
        }
        // Single container or page evaluation
        else if (results?.findings) {
            results.findings.forEach((finding: IContentEvaluation.EvaluationFinding) => {
                allFindings.push({
                    flaggedRule: finding.flaggedRule,
                    flaggedPassage: finding.flaggedPassage,
                    description: finding.description,
                    suggestedPassages: Array.isArray(finding.suggestedPassages)
                        ? finding.suggestedPassages.join("; ")
                        : (finding.suggestedPassages || "")
                });
            });
        }

        return allFindings;
    }

    private static getCSVHeaders(selectedContainerIds?: string[]): any {
        const baseHeaders = {
            flaggedRule: "Rule",
            flaggedPassage: "Flagged Passage",
            description: "Description",
            suggestedPassages: "Revised Example"
        };

        // Include container column for multi-container evaluations
        if (selectedContainerIds && selectedContainerIds.length > 0) {
            return {
                containerName: "Container",
                ...baseHeaders
            };
        }

        return baseHeaders;
    }

    private static generateFilename(entityName: string, selectedContainerIds?: string[]): string {
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const sanitizedName = entityName.replace(/[^a-zA-Z0-9]/g, "_");

            return `content_evaluation_${sanitizedName}_${timestamp}.csv`;
        }
}

interface ExportEvaluationFinding extends Omit<IContentEvaluation.EvaluationFinding, "suggestedPassages"> {
    suggestedPassages: string;
    containerName?: string;
}
